import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useTokens,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { RichTextContent } from './shared/RichTextContent';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio } from '../utils/luminance';

// ─── Slot group definitions ─────────────────────────────────────────────────
// Featured plan image + alt bind to the first floorplan by default so the
// component "just works" on any property, but each field is independently
// overridable in the editor.

const planImageDef: SlotGroupDefinition = {
  label: 'Featured Plan Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'floorplans[0].floorplan_image_url',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_name',
    },
  },
  sourceBinding: {
    collection: 'floorplans',
    defaultIndex: 0,
    fieldMappings: { image: 'floorplan_image_url', alt: 'floorplan_name' },
  },
};

const availabilityCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA (Check Availability)',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
      suggestedFields: ['property_apply_now_url', 'property_online_leasing_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Check Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const tourCtaDef: SlotGroupDefinition = {
  label: 'Secondary CTA (Schedule a Tour)',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/schedule-a-tour',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Schedule a Tour',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function FeaturedFloorPlan() {
  const planImage = useSlotGroup('featuredfloorplan.plan_image', planImageDef);
  const availabilityCta = useSlotGroup(
    'featuredfloorplan.availability_cta',
    availabilityCtaDef,
  );
  const tourCta = useSlotGroup('featuredfloorplan.tour_cta', tourCtaDef);

  // Spec values default to the first floorplan's fields so this page is
  // publishable immediately. Editors can retarget or hand-write any value.
  // Defaults are declared once in the contentSchema below — the schema-aware
  // runtime resolves them for both the sidebar and the inline editor.
  const planName = useContentToken('featuredfloorplan.plan_name');
  const beds = useContentToken('featuredfloorplan.beds');
  const baths = useContentToken('featuredfloorplan.baths');
  const sqft = useContentToken('featuredfloorplan.sqft');
  const price = useContentToken('featuredfloorplan.price');

  // Match the "CTA over white" treatment used in ApplyNowActions /
  // PropertyOverview — primary accent fallback to text when the primary color
  // fails WCAG contrast against a near-white background.
  const tokens = useTokens();
  const ctaAccent = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return 'var(--vibe-color-primary)';
    return contrastRatio(primary, background) >= 4.5
      ? 'var(--vibe-color-primary)'
      : 'var(--vibe-color-text)';
  }, [tokens]);

  const bedsLabel = formatBeds(beds);
  const bathsLabel = formatBaths(baths);
  const sqftLabel = formatSqft(sqft);
  const priceLabel = formatPrice(price);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up ffp-section"
      style={{ ['--rise-ffp-cta-accent' as string]: ctaAccent }}
    >
      <div className="ffp-container">
        <div className="ffp-grid">
          {/* Plan image panel */}
          <EditableSlotGroup
            groupPath="featuredfloorplan.plan_image"
            definition={planImageDef}
            className="ffp-image-wrap"
          >
            {planImage.image ? (
              <VibesitesImage
                url={planImage.image}
                alt={planImage.alt || `${planName || 'Featured'} floor plan`}
                sizes="(min-width: 1024px) 50vw, 100vw"
                className="ffp-image"
              />
            ) : (
              <div className="ffp-image-placeholder">
                <span>Floor plan coming soon</span>
              </div>
            )}
          </EditableSlotGroup>

          {/* Details panel */}
          <div className="ffp-details">
            <EditableToken
              path="featuredfloorplan.plan_name"
              as="h2"
              className="ffp-title"
            >
              {planName || 'Featured Floor Plan'}
            </EditableToken>

            <div className="ffp-divider" />

            <ul className="ffp-specs" aria-label="Floor plan specifications">
              {bedsLabel && (
                <li className="ffp-spec">
                  <SpecIcon kind="bed" />
                  <span className="ffp-spec-value">
                    <EditableToken path="featuredfloorplan.beds" as="span">
                      {bedsLabel}
                    </EditableToken>
                  </span>
                </li>
              )}
              {bathsLabel && (
                <li className="ffp-spec">
                  <SpecIcon kind="bath" />
                  <span className="ffp-spec-value">
                    <EditableToken path="featuredfloorplan.baths" as="span">
                      {bathsLabel}
                    </EditableToken>
                  </span>
                </li>
              )}
              {sqftLabel && (
                <li className="ffp-spec">
                  <SpecIcon kind="sqft" />
                  <span className="ffp-spec-value">
                    <EditableToken path="featuredfloorplan.sqft" as="span">
                      {sqftLabel}
                    </EditableToken>
                  </span>
                </li>
              )}
            </ul>

            <p className="ffp-price">
              <EditableToken path="featuredfloorplan.price" as="span">
                {priceLabel}
              </EditableToken>
            </p>

            <RichTextContent
              path="featuredfloorplan.description"
              defaultValue={
                '<p>Step inside our featured layout at ' +
                '<span data-type="field-token" data-field="property_name"></span>. ' +
                'This plan was hand-picked for its smart use of space, generous natural light, and the features our residents love most. ' +
                'Browse the floor plan, imagine your furniture in place, and reach out when you&rsquo;re ready to tour.</p>'
              }
              as="div"
              className="ffp-description"
            />

            <div className="ffp-actions">
              <EditableSlotGroup
                groupPath="featuredfloorplan.availability_cta"
                definition={availabilityCtaDef}
              >
                <a
                  href={availabilityCta.url || '/floor-plans'}
                  target={availabilityCta._target || '_self'}
                  rel={
                    availabilityCta._target === '_blank'
                      ? 'noopener noreferrer'
                      : undefined
                  }
                  className="ffp-btn ffp-btn--primary"
                >
                  <span className="ffp-btn-label">
                    {availabilityCta.label || 'Check Availability'}
                  </span>
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup
                groupPath="featuredfloorplan.tour_cta"
                definition={tourCtaDef}
              >
                <a
                  href={tourCta.url || '/schedule-a-tour'}
                  target={tourCta._target || '_self'}
                  rel={
                    tourCta._target === '_blank' ? 'noopener noreferrer' : undefined
                  }
                  className="ffp-btn ffp-btn--secondary"
                >
                  <span className="ffp-btn-label">
                    {tourCta.label || 'Schedule a Tour'}
                  </span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ffp-section {
          padding: 3rem 0 3.5rem;
          background: var(--vibe-color-background, #ffffff);
        }
        @media (min-width: 768px) {
          .ffp-section { padding: 4rem 0 5rem; }
        }

        .ffp-container {
          max-width: 80rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .ffp-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .ffp-container { padding: 0 2rem; } }

        .ffp-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          align-items: center;
        }
        @media (min-width: 1024px) {
          .ffp-grid {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 3.5rem;
          }
        }

        /* ── Image panel ── */
        .ffp-image-wrap {
          position: relative;
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 4px;
          padding: 1.25rem;
          aspect-ratio: 4 / 3;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }
        @media (min-width: 768px) {
          .ffp-image-wrap { padding: 2rem; }
        }
        .ffp-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .ffp-image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--vibe-color-surface, #f5f5f5);
          font-family: var(--vibe-font-heading);
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: var(--vibe-color-text-muted, #6b7280);
        }

        /* ── Details panel ── */
        .ffp-details {
          display: flex;
          flex-direction: column;
        }

        .ffp-title {
          font-family: var(--vibe-font-heading);
          font-size: 2rem;
          font-weight: 700;
          line-height: 1.15;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: var(--vibe-color-text, #111);
          margin: 0;
        }
        @media (min-width: 768px) {
          .ffp-title { font-size: 2.5rem; }
        }

        .ffp-divider {
          width: 60px;
          height: 3px;
          background: var(--vibe-color-primary, #2b2b2b);
          margin: 1.25rem 0;
          border-radius: 2px;
        }

        /* ── Specs row ── */
        .ffp-specs {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem 1.75rem;
          margin: 0 0 1.25rem;
          padding: 0;
          list-style: none;
        }
        .ffp-spec {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--vibe-color-text, #111);
        }
        .ffp-spec svg {
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          color: var(--vibe-color-primary, #2b2b2b);
        }

        .ffp-price {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--rise-ffp-cta-accent, var(--vibe-color-primary));
          margin: 0 0 1.5rem;
        }
        @media (min-width: 768px) {
          .ffp-price { font-size: 1.5rem; }
        }

        .ffp-description {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          line-height: 1.7;
          color: var(--vibe-color-text, #111);
          margin: 0 0 2rem;
        }
        .ffp-description :is(a) {
          color: var(--vibe-color-primary, #2b2b2b);
          text-decoration: underline;
        }

        /* ── Actions ── */
        .ffp-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-top: auto;
        }
        @media (min-width: 480px) {
          .ffp-actions { flex-direction: row; gap: 1rem; }
        }

        /* Matches the shared outline-fill CTA used in ApplyNowActions and
           FloorplanShowcase: accent border + label at rest, fills with
           secondary on hover. */
        .ffp-btn {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          padding: 0.875rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          cursor: pointer;
          background: transparent;
          border: 1.5px solid var(--rise-ffp-cta-accent, var(--vibe-color-primary));
          color: var(--rise-ffp-cta-accent, var(--vibe-color-primary));
          transition: color 0.3s ease, transform 0.3s ease,
            box-shadow 0.3s ease, border-color 0.3s ease, background 0.3s ease;
        }
        .ffp-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vibe-color-secondary);
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 0;
        }
        .ffp-btn-label {
          position: relative;
          z-index: 1;
        }
        .ffp-btn:hover {
          color: var(--vibe-color-text-on-secondary);
          border-color: var(--vibe-color-secondary);
          transform: translateY(-2px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 30%, transparent);
        }
        .ffp-btn:hover::before { opacity: 1; }
        .ffp-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* Secondary CTA — same geometry, muted border at rest */
        .ffp-btn--secondary {
          border-color: color-mix(in srgb, var(--rise-ffp-cta-accent, var(--vibe-color-primary)) 50%, transparent);
          color: var(--vibe-color-text, #111);
        }
      `}</style>
    </section>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function toNumber(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatBeds(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  const lower = trimmed.toLowerCase();
  if (lower === '0' || lower === 'studio' || lower === 'std') return 'Studio';
  const n = toNumber(trimmed);
  if (n == null) return trimmed;
  return `${n} ${n === 1 ? 'Bed' : 'Beds'}`;
}

function formatBaths(raw: string | null | undefined): string {
  const n = toNumber(raw);
  if (n == null) return raw ? String(raw) : '';
  return `${n} ${n === 1 ? 'Bath' : 'Baths'}`;
}

function formatSqft(raw: string | null | undefined): string {
  const n = toNumber(raw);
  if (n == null) return raw ? String(raw) : '';
  return `${n.toLocaleString()} Sq Ft`;
}

function formatPrice(raw: string | null | undefined): string {
  const n = toNumber(raw);
  if (n == null) return 'Call for Pricing';
  return `Starting at $${Math.round(n).toLocaleString()}`;
}

// ─── Spec icons ─────────────────────────────────────────────────────────────

function SpecIcon({ kind }: { kind: 'bed' | 'bath' | 'sqft' }) {
  if (kind === 'bed') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M3 10V6a1 1 0 011-1h5a1 1 0 011 1v4h8a3 3 0 013 3v6h-2v-2H5v2H3V10zm2 2v3h14v-2a1 1 0 00-1-1H5zm2-6v4h2V7a1 1 0 011-1h-3z"
          fill="currentColor"
        />
      </svg>
    );
  }
  if (kind === 'bath') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M20 10V5.5a1.5 1.5 0 10-3 0V6h-2V5.5a3.5 3.5 0 017 0V10h1v2h-1v3a4 4 0 01-4 4H6a4 4 0 01-4-4v-3H1v-2h19zM4 12v3a2 2 0 002 2h12a2 2 0 002-2v-3H4z"
          fill="currentColor"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 3h7v2H5v5H3V3zm11 0h7v7h-2V5h-5V3zM3 14h2v5h5v2H3v-7zm18 0v7h-7v-2h5v-5h2z"
        fill="currentColor"
      />
    </svg>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeaturedFloorPlan',
  displayName: 'Featured Floor Plan',
  tokens: {
    'featuredfloorplan.plan_name': {
      label: 'Plan Name',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_name',
      defaultValue: 'Featured Floor Plan',
      suggestedFields: ['floorplans[0].floorplan_name'],
    },
    'featuredfloorplan.beds': {
      label: 'Bedrooms',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_beds_min',
      defaultValue: '',
    },
    'featuredfloorplan.baths': {
      label: 'Bathrooms',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_baths_min',
      defaultValue: '',
    },
    'featuredfloorplan.sqft': {
      label: 'Square Feet',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_sqft_min',
      defaultValue: '',
    },
    'featuredfloorplan.price': {
      label: 'Starting Price',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_price_min',
      defaultValue: '',
    },
    'featuredfloorplan.description': {
      label: 'Description',
      type: 'richtext',
      defaultValue:
        '<p>Step inside our featured layout at <span data-type="field-token" data-field="property_name"></span>. This plan was hand-picked for its smart use of space, generous natural light, and the features our residents love most. Browse the floor plan, imagine your furniture in place, and reach out when you&rsquo;re ready to tour.</p>',
    },
  },
  slotGroups: {
    'featuredfloorplan.plan_image': planImageDef,
    'featuredfloorplan.availability_cta': availabilityCtaDef,
    'featuredfloorplan.tour_cta': tourCtaDef,
  },
};

(FeaturedFloorPlan as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeaturedFloorPlan;
